import { ConnectedRouter } from 'connected-react-router';
import { History } from 'history';
import { lazy } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { isMarketingHost } from './utils/marketingHost';

// Route-level code splitting: each route is loaded on demand
const Login = lazy(() => import('./containers/Authentication/Login'));
const RequestPasswordReset = lazy(() => import('./containers/Authentication/ForgotPassword/RequestPasswordReset'));
const ConfirmPasswordReset = lazy(() => import('./containers/Authentication/ForgotPassword/ConfirmPasswordReset'));
const Apartments = lazy(() => import('./containers/Apartments/Apartments'));
const NewApplication = lazy(() => import('./containers/NewApplication/NewApplication'));
const ApplicationPayment = lazy(() => import('./containers/NewApplication/ApplicationPayment'));
const Applications = lazy(() => import('./containers/Applications'));
const Messages = lazy(() => import('./containers/Messages'));
const CreateShowings = lazy(() => import('./containers/Showings/Create'));
const OpenShowingPage = lazy(() => import('./containers/Showings/Open'));
const Stepper = lazy(() => import('./containers/Apartment/Stepper'));
const BeforeLoginStepper = lazy(() => import('./containers/Apartment/BeforeLoginStepper/BeforeLoginStepper'));
const ApartmentOverview = lazy(() => import('./containers/Apartment/Overview'));
const ApartmentStatistics = lazy(() => import('./containers/Apartment/Statistics'));
const EditApplication = lazy(() => import('./containers/Application/EditApplication/EditApplication'));
const ReserveShowing = lazy(() => import('./containers/Showings/Reserve/ReserveShowing'));
const SuggestShowingTimes = lazy(() => import('./containers/Showings/Reserve/SuggestShowingTimes'));
const TenantHome = lazy(() => import('./containers/TenantHome/TenantHome'));
const Profile = lazy(() => import('./containers/Profile'));
const ContractGeneratorStepper = lazy(() => import('./containers/ContractGenerator'));
const Payment = lazy(() => import('./containers/PayForPublish/Payment'));
const AfterShowingStepper = lazy(() => import('./containers/AfterShowing'));
const PurchaseOfferStepper = lazy(() => import('./containers/PurchaseOffer'));
const LandlordIdentifier = lazy(() => import('./containers/LandlordIdentifier/index'));
const PublicListing = lazy(() => import('./containers/Apartment/Overview/PublicListing'));
const Settings = lazy(() => import('./containers/Settings/Settings'));
const ApplicationSubmitted = lazy(() => import('./containers/ApplicationSubmitted/ApplicationSubmitted'));
const Activate = lazy(() => import('./containers/Activate/Activate'));
const Reactivate = lazy(() => import('./containers/Activate/Reactivate'));
const PrivacyPolicy = lazy(() => import('./containers/PrivacyPolicy/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./containers/TermsOfService/TermsOfService'));
const Info = lazy(() => import('./containers/Info/Info'));
const NotFound = lazy(() => import('./containers/NotFound/NotFound'));
const Routes = () => (<Switch>
    {/* Juuriosoitteessa (fiksumyynti.fi) etusivu on infosivu ilman ohjausta; sovellusosoitteessa kirjautuminen */}
    <Route exact path='/' component={isMarketingHost() ? Info : Login}/>
    <Route exact path='/info' component={Info}/>
    <Redirect exact from='/signup' to='/create/apartment'/>
    <Route exact path='/forgotpassword/request' component={RequestPasswordReset}/>
    <Route exact path='/forgotpassword/reset/:email/:code' component={ConfirmPasswordReset}/>
    <Route exact path='/apartments' component={Apartments}/>
    <Route exact path='/adminApartments/:status' component={Apartments}/>
    <Route exact path='/adminApartments' component={Apartments}/>
    <Route exact path='/new-application/:apartmentCode' component={NewApplication}/>
    <Route exact path='/new-application/:apartmentCode/steps/:step' component={NewApplication}/>
    <Route exact path='/application/:applicationCode/payment' component={ApplicationPayment}/>
    <Route exact path='/users/:userId/apartments/:apartmentId/applications' component={Applications}/>
    <Route exact path='/users/:userId/apartments/:apartmentId/messages' component={Messages}/>
    <Route exact path='/users/:userId/apartments/:apartmentId/showings/open' component={OpenShowingPage}/>
    <Route exact path='/users/:userId/apartments/:apartmentId/showings' component={CreateShowings}/>
    <Route exact path='/users/:userId/apartments/:apartmentId/steps/:step' component={Stepper}/>
    {/* <Redirect exact from='/instant-rent/:step' to='/instant-rent/steps/0' /> */}
    {/* <Route
      exact
      path='/instant-rent/:apartmentCode/steps/:step'
      component={InstantRentApplicationStepper}
    /> */}
    <Redirect exact from='/create/apartment' to='/create/apartment/steps/0'/>
    <Route exact path='/create/apartment/steps/:step' component={BeforeLoginStepper}/>
    <Route exact path='/users/:userId/apartments/:apartmentId/overview' component={ApartmentOverview}/>
    <Route exact path='/users/:userId/apartments/:apartmentId/statistic' component={ApartmentStatistics}/>
    <Route exact path='/application/:applicationId/apartments/:apartmentId/edit' component={EditApplication}/>
    <Route exact path='/reserve/:applicationCode' component={ReserveShowing}/>
    <Route exact path='/reserve/:applicationCode/suggest' component={SuggestShowingTimes}/>
    <Route exact path='/home' component={TenantHome}/>
    <Route exact path='/profile' component={Profile}/>
    <Route exact path='/messages' component={Messages}/>
    <Route exact path='/users/:userId/apartments/:apartmentId/contract/:applicationCode/steps/:step' component={ContractGeneratorStepper}/>
    <Route exact path='/users/:userId/apartments/:apartmentId/payment' component={Payment}/>
    <Route exact path='/aftershowing/:applicationCode/steps/:step' component={AfterShowingStepper}/>
    <Route exact path='/users/:userId/verification' component={LandlordIdentifier}/>
    <Route exact path='/listing/:apartmentCode' component={PublicListing}/>
    <Redirect exact from='/tenant/:applicationCode' to='/home'/>
    <Route exact path="/aftershowing/:applicationCode" render={({ match }) => (<Redirect to={`/aftershowing/${match.params.applicationCode}/steps/1`}/>)}/>
    <Route exact path='/purchase-offer/:applicationCode/steps/:step' component={PurchaseOfferStepper}/>
    <Route exact path="/purchase-offer/:applicationCode" render={({ match }) => (<Redirect to={`/purchase-offer/${match.params.applicationCode}/steps/1`}/>)}/>
    <Route exact path='/generatecontract/:applicationCode/steps/:step' component={ContractGeneratorStepper}/>
    <Route exact path='/privacy-policy' component={PrivacyPolicy}/>
    <Route exact path='/terms-of-service' component={TermsOfService}/>
    <Route exact path='/settings' component={Settings}/>
    <Route exact path='/application-submitted' component={ApplicationSubmitted}/>
    <Route exact path='/reactivate' component={Reactivate}/>
    <Route exact path='/activate/:token' component={Activate}/>
    <Route path='*' component={NotFound}/>
  </Switch>);
const Navigation = lazy(() => import('./containers/Navigation'));
const AppRouter = ({ history }: {
    history: History<unknown>;
}) => {
    return (<ConnectedRouter history={history}>
      <Navigation Routes={Routes}/>
    </ConnectedRouter>);
};
export default AppRouter;
